/**
 * Identity Domain Contracts
 *
 * Re-exports Zod schemas from @virial-ai/core for runtime validation.
 * This avoids duplication and ensures alignment with the API contract.
 *
 * Architecture:
 * - Single source of truth: @virial-ai/core
 * - No code duplication or drift
 * - Runtime validation of API responses
 * - Used in services to validate responses
 *
 * @see @virial-ai/core/identity for the source schemas
 */

import { z } from "zod";
import { identitySchemas as coreIdentitySchemas } from "@virial-ai/core/identity";

/**
 * Re-export schemas from core
 * Core uses Org, ListUsersResponse, GetOrgByIdResponse (no PlatformConstraint/UIConfig in schemas).
 */
export const {
  ResponseMeta,
  PageInfo,
  User,
  Org,
  ListUsersResponse,
  GetUserResponse,
  GetOrgByIdResponse,
} = coreIdentitySchemas;

/**
 * UIConfig response schema (not in core identity OpenAPI; used by platform.service for /platform/ui-configs/:configType).
 * Validates { data: unknown, meta?: object } so getUIConfig can validate API responses.
 */
export const UIConfigResponseSchema = z
  .object({
    data: z.unknown().optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();

/**
 * Export all schemas as a namespace for convenience
 * This provides a consistent API for accessing all identity schemas
 */
export const identitySchemas = coreIdentitySchemas;

/**
 * Response type exports (inferred from schemas)
 */
export type ListUsersResponseType = z.infer<typeof ListUsersResponse>;
export type UserResponse = z.infer<typeof GetUserResponse>;
export type GetOrgByIdResponseType = z.infer<typeof GetOrgByIdResponse>;
